import { ReCaptcha } from "@client/@types/recaptcha";
import { recaptchaProtectedApi } from "@client/api";
import React from "react";

import {
    CmsFooterContent,
    CmsHeaderContent,
    Context,
    ServerCmsModelsPagesBaseBreadcrumb,
} from "@reactivated";

import { type constants } from "@reactivated/constants";

import { MetaTagsBlock } from "@thelabnyc/thelabui/src/components/MetaTagsBlock";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Breadcrumbs } from "../Breadcrumbs";
import { Footer } from "../Footer";
import { NewsletterProvider } from "../Footer/NewsletterSignUp";
import { HeaderNav } from "../Header";
import { SocialContext } from "../SocialIcons";

import styles from "./index.module.scss";

import "../../styles/global.scss";

type ThemeEnum =
    (typeof constants)["server.cms.constants.BackgroundThemeChoices"];

interface Props {
    title: string;
    seo_title?: string;
    search_description?: string;
    robots?: string;
    rootClassName?: string;
    children?: React.ReactNode;
    footer_override?: CmsFooterContent;
    header_override?: CmsHeaderContent;
    breadcrumbs?: ServerCmsModelsPagesBaseBreadcrumb[];
    breadcrumbs_theme?: ThemeEnum[keyof ThemeEnum];
    show_breadcrumbs?: boolean;
}
declare global {
    interface Window {
        grecaptcha?: ReCaptcha;
    }
}

export const PageSkeleton = ({
    breadcrumbs,
    breadcrumbs_theme = "robinsBlue",
    ...props
}: Props) => {
    const context = React.useContext(Context);
    const footer_data = props.footer_override ?? context.footer_content?.body;
    const header_data = props.header_override ?? context.header_content?.body;
    const api = recaptchaProtectedApi(context.recaptcha_key);

    React.useEffect(() => {
        // Only add recaptcha's js once
        const src = `https://www.google.com/recaptcha/api.js?render=${context.recaptcha_key}`;
        const rcScriptId = "recaptcha-script";
        if (!document.getElementById(rcScriptId)) {
            const script = document.createElement("script");
            script.src = src;
            script.id = rcScriptId;
            document.head.appendChild(script);
        }
    });

    return (
        <html lang="en">
            <head>
                <meta charSet="utf-8" />
                <title>{props.seo_title || props.title}</title>
                {props.search_description && (
                    <meta
                        name="description"
                        content={props.search_description}
                    />
                )}
                {props.robots && <meta name="robots" content={props.robots} />}
                {MetaTagsBlock({
                    tags: context.meta_tags.meta_tags.value,
                })}
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1, viewport-fit=cover"
                />
                {/* TODO restore these with a logo that the client prefers */}
                {/* <link rel="shortcut icon" href={context.core_assets.favicon} />
                <link
                    rel="icon"
                    type="image/svg+xml"
                    href={context.core_assets.favicon_svg}
                />
                <link
                    rel="icon"
                    type="image/png"
                    href={context.core_assets.favicon_96}
                    sizes="96x96"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="180x180"
                    href={context.core_assets.apple_touch_icon}
                /> */}
                <meta name="apple-mobile-web-app-title" content="Goodcook" />
                <link rel="manifest" href={context.core_assets.webmanifest} />
            </head>
            <body>
                <SocialContext value={context.socials}>
                    <NewsletterProvider
                        registerForNewsletter={api.registerForNewsletter}
                    >
                        {header_data && <HeaderNav content={header_data} />}
                        <div
                            className={concatClassNames([
                                styles.container,
                                props.rootClassName,
                            ])}
                        >
                            {props.show_breadcrumbs && breadcrumbs && (
                                <Breadcrumbs
                                    value={breadcrumbs}
                                    theme={breadcrumbs_theme}
                                />
                            )}
                            {props.children}
                            {footer_data && <Footer content={footer_data} />}
                        </div>
                    </NewsletterProvider>
                </SocialContext>
            </body>
        </html>
    );
};
